import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  SelectChangeEvent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { handlePreviewFile } from 'src/services/common';

interface BidResultsDetailsProps {
  bidId: string;
  bidDetails: any;
  onBack: () => void;
}

// Interface for PO document data
interface PODocument {
  PONumber: string;
  POFilePath: string;
  DocumentType: string;
  CreateDate: string;
  DocumentUrl: string;
}

// Type for the column sorting
type Order = 'asc' | 'desc';
type PODocumentKeys = keyof PODocument | 'formattedDate';

const UploadPODetails: React.FC<BidResultsDetailsProps> = ({
  bidId,
  bidDetails,
  onBack
}) => {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [poNumber, setPoNumber] = useState<string>(''); // PO Number state
  const [awardAmount, setAwardAmount] = useState<number | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // States for vendor and contact selection
  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);
  const [loadingVendors, setLoadingVendors] = useState<boolean>(false);
  const [vendorError, setVendorError] = useState<string | null>(null);
  const isVendorPrePopulated = !!(
    bidDetails?.awardedToBusiness && bidDetails?.awardedToId
  );

  const [contacts, setContacts] = useState<
    {
      id: string;
      name: string;
      userId: string; // Added UserID to contacts
    }[]
  >([]);
  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);
  const [contactError, setContactError] = useState<string | null>(null);

  const [selectedVendor, setSelectedVendor] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<string>('');

  const fetchedVendorsRef = useRef<Set<string>>(new Set());

  // PO Documents table states
  const [poDocuments, setPoDocuments] = useState<PODocument[]>([]);
  const [loadingPODocuments, setLoadingPODocuments] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<PODocumentKeys>('CreateDate');

  const fetchVendors = useCallback(async () => {
    setLoadingVendors(true);
    setVendorError(null);

    try {
      const response = await httpRequest(
        'GET',
        '/auth/vendors_information',
        {},
        {}
      );

      if (response.status === 'success' && response.data?.BusinessDetails) {
        const vendorsList = response.data.BusinessDetails.map(
          (vendor: { Business: string }, index: number) => ({
            id: `vendor-${index}`,
            name: vendor.Business
          })
        );
        setVendors(vendorsList);
      } else {
        setVendorError(
          response.message ||
            'Failed to load vendors. Unexpected response format.'
        );
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setVendorError('Failed to load vendors. Please try again.');
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  const fetchContacts = async (vendorName: string) => {
    if (!vendorName) return;

    // Clear existing contacts before fetching new ones
    setContacts([]);
    setContactError(null);

    // Skip fetching if contacts are already loading
    if (loadingContacts) return;

    setLoadingContacts(true);

    try {
      // Avoid calling API again for the same vendor if it's already fetched
      if (fetchedVendorsRef.current.has(vendorName)) {
        return;
      }

      // Add the vendor to the ref to track that it has been fetched
      fetchedVendorsRef.current.add(vendorName);

      // Build the API request URL
      const url = `/auth/vendors_information?Business=${encodeURIComponent(
        vendorName
      )}${bidId ? `&BidID=${bidId}` : ''}`;

      // Make the API call to fetch vendor details
      const response = await httpRequest('GET', url, {}, {});

      if (response.status === 'success') {
        // If the response has VendorDetails, map them to contacts
        if (
          response.data?.VendorDetails &&
          Array.isArray(response.data.VendorDetails)
        ) {
          const contactsList = response.data.VendorDetails.map(
            (contact: any, index: number) => ({
              id: `contact-${contact.UserID || index}`,
              name:
                contact.FullNameWithEmail ||
                `${contact.FirstName} ${contact.LastName} - ${contact.EmailID}`,
              userId: contact.UserID // Store the UserID for API submission
            })
          );

          setContacts(contactsList);

          // Automatically select the first contact if no contact is selected
          if (contactsList.length > 0 && !selectedContact) {
            setSelectedContact(contactsList[0].userId); // Set the first contact as selected
          }
        } else {
          setContacts([]); // If no contacts available, set an empty list
        }
      } else {
        setContactError('Failed to load contacts. Unexpected response format.');
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContactError('Failed to load contacts. Please try again.');
    } finally {
      setLoadingContacts(false); // Reset loading state
    }
  };

  // Fetch PO documents
  const fetchPODocuments = useCallback(async () => {
    if (!bidId) return;

    setLoadingPODocuments(true);
    try {
      const response = await httpRequest(
        'GET',
        `bid_portal/bid_details/${bidId}/PO`,
        {},
        {}
      );

      if (response.status === 'success' && response.data) {
        setPoDocuments(response.data);
      } else {
        console.error('Failed to fetch PO documents');
        setPoDocuments([]);
      }
    } catch (error) {
      console.error('Error fetching PO documents:', error);
      setPoDocuments([]);
    } finally {
      setLoadingPODocuments(false);
    }
  }, [bidId]);

  // Handle sorting for PO documents table
  const handleRequestSort = (property: PODocumentKeys) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sort function
  const stableSort = <T extends object>(
    array: T[],
    comparator: (a: T, b: T) => number
  ) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const compareResult = comparator(a[0], b[0]);
      if (compareResult !== 0) return compareResult;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const descendingComparator = <T extends object>(
    a: T,
    b: T,
    property: keyof T
  ) => {
    if (b[property] < a[property]) {
      return -1;
    }
    if (b[property] > a[property]) {
      return 1;
    }
    return 0;
  };

  const getComparator = <Key extends keyof any>(
    sortOrder: Order,
    sortOrderBy: Key
  ): ((a: { [key in Key]: any }, b: { [key in Key]: any }) => number) => {
    return sortOrder === 'desc'
      ? (a, b) => descendingComparator(a, b, sortOrderBy)
      : (a, b) => -descendingComparator(a, b, sortOrderBy);
  };

  // Function to format create date
  const formatCreateDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  // Function to get documents array with formatted dates for sorting
  const getDocumentsWithFormattedDates = () => {
    return poDocuments.map((doc) => ({
      ...doc,
      formattedDate: formatCreateDate(doc.CreateDate)
    }));
  };

  const handleVendorChange = (
    event: SelectChangeEvent<string> // Use SelectChangeEvent instead of React.ChangeEvent
  ) => {
    const newSelectedVendor = event.target.value;
    setSelectedVendor(newSelectedVendor);
    setSelectedContact(''); // Clear the contact when vendor changes

    // Reset contacts before fetching new ones
    setContacts([]);
    setContactError(null);

    // Clear the vendor from the ref so it can be refetched
    fetchedVendorsRef.current.delete(newSelectedVendor);

    // Always fetch contacts for the selected vendor
    fetchContacts(newSelectedVendor);
  };

  const handleContactChange = (
    event: SelectChangeEvent<string> // Use SelectChangeEvent instead of React.ChangeEvent
  ) => {
    setSelectedContact(event.target.value);
  };

  // File validation function
  const validateFile = (file: File): string | null => {
    // Check file type
    const allowedExtensions = [
      '.pdf',
      '.xls',
      '.xlsx',
      '.doc',
      '.docx',
      '.zip'
    ];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf('.'))
      .toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      return `Invalid file type. Only ${allowedExtensions.join(
        ', '
      )} files are allowed.`;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      return 'File is too large. Maximum file size is 10MB.';
    }

    return null;
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle file drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      const validationError = validateFile(file);

      if (validationError) {
        setFileError(validationError);
        toast.error(validationError);
        setTimeout(() => setFileError(null), 5000);
      } else {
        setSelectedFile(file);
        setFileError(null);
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      if (file) {
        const validationError = validateFile(file);

        if (validationError) {
          setFileError(validationError);
          toast.error(validationError);
          setTimeout(() => setFileError(null), 5000);
        } else {
          setSelectedFile(file);
          setFileError(null);
        }
      }
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setPoNumber('');
    setAwardAmount(null);
    setFileError(null);
    // Only clear vendor and contact if not pre-populated
    if (!isVendorPrePopulated) {
      setSelectedVendor('');
      setSelectedContact('');
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile || !poNumber || !selectedVendor || !selectedContact) {
      toast.error('Please fill all required fields.');
      return;
    }

    setIsLoading(true);

    const uploadFormData = new FormData();
    uploadFormData.append('UploadBidsDocument', selectedFile);
    uploadFormData.append('DocumentDescription', 'Bid Result Document');

    try {
      const response = await httpRequest(
        'POST',
        '/bid_portal/bids/upload_document',
        uploadFormData,
        { 'Content-Type': 'multipart/form-data' }
      );

      if (response.type === 'NETWORK_ERROR' || response.status !== 'success') {
        throw new Error(
          response.message || `Failed to upload document: ${selectedFile.name}`
        );
      }

      const payload = {
        BidID: bidId || bidDetails.contractNo,
        AwardedTo: selectedContact, // Send UserID to the API
        Business: selectedVendor,
        POFile: {
          FileName: response.data?.DocumentName,
          Type: response?.data?.FileType
        },
        PONumber: poNumber,
        BidTitle: bidDetails.title,
        ContractNo: bidDetails.contractNo,
        ...(awardAmount !== null && { AwardAmount: awardAmount })
      };

      const resultResponse = await httpRequest(
        'POST',
        '/bid_portal/bid_details/upload_PO',
        payload
      );

      if (resultResponse?.status === 'success') {
        toast.success('PO file uploaded successfully!');
        handleClear();
        // Refresh PO documents after successful upload
        fetchPODocuments();
      } else {
        toast.error(resultResponse.message || 'File result upload failed.');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  // Effect to handle pre-population when component mounts
  useEffect(() => {
    if (isVendorPrePopulated) {
      setSelectedVendor(bidDetails.awardedToBusiness!);

      if (bidDetails.awardedToContact?.UserID) {
        setSelectedContact(bidDetails.awardedToContact.UserID.toString());

        const prePopulatedContact = {
          id: `contact-${bidDetails.awardedToContact.UserID}`,
          name: `${bidDetails.awardedToContact.FirstName} ${bidDetails.awardedToContact.LastName} - ${bidDetails.awardedToContact.EmailID}`,
          userId: bidDetails.awardedToContact.UserID.toString()
        };
        setContacts([prePopulatedContact]);
      } else {
        fetchContacts(bidDetails.awardedToBusiness!);
      }
    }
  }, [bidDetails, isVendorPrePopulated]);

  // Fetch PO documents on component mount
  useEffect(() => {
    fetchPODocuments();
  }, [fetchPODocuments]);

  // Get documents with formatted dates for display and sorting
  const documentsWithFormattedDates = getDocumentsWithFormattedDates();

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton
          onClick={onBack}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2)
            }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 80,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          PO Upload
        </Typography>
      </Box>

      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5
                }}
              >
                Contract No.: {bidDetails.contractNo}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5
                }}
              >
                Title: {bidDetails.title}
              </Typography>
            </Box>

            <Box>
              <FormControl fullWidth error={!!vendorError} required>
                <InputLabel>Awarded To (Vendor)</InputLabel>
                <Select
                  value={selectedVendor}
                  onChange={handleVendorChange}
                  label="Awarded To (Vendor)"
                  disabled={loadingVendors || isVendorPrePopulated}
                  startAdornment={
                    loadingVendors ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : null
                  }
                >
                  {isVendorPrePopulated ? (
                    // Show only the pre-populated vendor
                    <MenuItem value={bidDetails.awardedToBusiness}>
                      {bidDetails.awardedToBusiness}
                    </MenuItem>
                  ) : (
                    // Show all vendors for selection
                    vendors.map((vendor) => (
                      <MenuItem key={vendor.id} value={vendor.name}>
                        {vendor.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {vendorError && <FormHelperText>{vendorError}</FormHelperText>}
              </FormControl>
            </Box>

            <Box>
              <FormControl fullWidth error={!!contactError} required>
                <InputLabel>Awarded To (Contact)</InputLabel>
                <Select
                  value={selectedContact}
                  onChange={handleContactChange}
                  label="Awarded To (Contact)"
                  disabled={loadingContacts || isVendorPrePopulated}
                  startAdornment={
                    loadingContacts ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : null
                  }
                >
                  {contacts.map((contact) => (
                    <MenuItem key={contact.id} value={contact.userId}>
                      {contact.name}
                    </MenuItem>
                  ))}
                </Select>
                {contactError && (
                  <FormHelperText>{contactError}</FormHelperText>
                )}
              </FormControl>
            </Box>

            <Box>
              <TextField
                label="PO#"
                fullWidth
                required
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
              />
            </Box>

            <Box>
              <TextField
                label="Award Amount"
                fullWidth
                type="number"
                value={
                  (awardAmount ? awardAmount : bidDetails.awardAmount) ?? ''
                }
                inputProps={{
                  step: '0.01', // allow 2 decimals
                  min: '0' // no negative numbers
                }}
                disabled={bidDetails.awardAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  setAwardAmount(value === '' ? null : parseFloat(value));
                }}
                onBlur={(e) => {
                  if (e.target.value) {
                    setAwardAmount(
                      parseFloat(parseFloat(e.target.value).toFixed(2))
                    ); // force 2 decimals
                  }
                }}
                onKeyDown={(e) => {
                  if (['e', 'E', '+', '-'].includes(e.key)) {
                    e.preventDefault(); // block invalid chars
                  }
                }}
              />
            </Box>

            {/* Drag & Drop Zone for PO File */}
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 1
                }}
              >
                PO File:{' '}
                <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <Paper
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                elevation={0}
                sx={{
                  p: 3,
                  border: dragActive
                    ? '2px dashed #1976d2'
                    : fileError
                    ? '2px dashed #d32f2f'
                    : selectedFile
                    ? '2px dashed #2e7d32'
                    : '2px dashed #ccc',
                  backgroundColor: dragActive
                    ? 'rgba(25, 118, 210, 0.08)'
                    : 'transparent',
                  borderRadius: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                <input
                  type="file"
                  hidden
                  id="po-file-input"
                  accept=".pdf,.xls,.xlsx,.doc,.docx,.zip"
                  onChange={handleFileChange}
                />

                <label
                  htmlFor="po-file-input"
                  style={{ cursor: 'pointer', display: 'block' }}
                >
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color: fileError
                        ? 'error.main'
                        : selectedFile
                        ? 'success.main'
                        : 'primary.main',
                      mb: 1
                    }}
                  />
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    sx={{ fontWeight: 500, mb: 0.5 }}
                  >
                    {selectedFile
                      ? 'Drop file to replace or click to browse'
                      : 'Drag & drop PO file here or click to browse'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Supported formats: .pdf, .xls, .xlsx, .doc, .docx, .zip
                    (Max: 10MB)
                  </Typography>
                </label>

                {selectedFile && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'success.main',
                        fontWeight: 'medium',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                      }}
                    >
                      ✅ Selected file: {selectedFile.name}
                    </Typography>
                  </Box>
                )}

                {fileError && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'error.main',
                        fontWeight: 'medium'
                      }}
                    >
                      ❌ {fileError}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isLoading}
              sx={{
                borderRadius: 1.5,
                px: 3,
                py: 1,
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.9)
                }
              }}
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClear}
              sx={{
                ml: 2,
                borderRadius: 1.5,
                px: 3,
                py: 1,
                fontWeight: 'bold'
              }}
            >
              Clear
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* PO Documents Table */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Uploaded PO Documents
        </Typography>
        {loadingPODocuments ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table aria-label="uploaded PO documents table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'PONumber'}
                      direction={orderBy === 'PONumber' ? order : 'asc'}
                      onClick={() => handleRequestSort('PONumber')}
                    >
                      PO Number
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'POFilePath'}
                      direction={orderBy === 'POFilePath' ? order : 'asc'}
                      onClick={() => handleRequestSort('POFilePath')}
                    >
                      File Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'CreateDate'}
                      direction={orderBy === 'CreateDate' ? order : 'asc'}
                      onClick={() => handleRequestSort('CreateDate')}
                    >
                      Upload Date & Time
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stableSort(
                  documentsWithFormattedDates,
                  getComparator(order, orderBy)
                )
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((doc, index) => (
                    <TableRow key={index}>
                      <TableCell>{doc.PONumber}</TableCell>
                      <TableCell>{doc.POFilePath}</TableCell>
                      <TableCell>{formatCreateDate(doc.CreateDate)}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handlePreviewFile(doc.DocumentUrl);
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                {documentsWithFormattedDates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography
                        sx={{
                          py: 2,
                          fontStyle: 'italic',
                          color: 'text.secondary'
                        }}
                      >
                        No PO documents found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={documentsWithFormattedDates.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default UploadPODetails;
